import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { clientApi } from '@/api/client'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'
import BackLink from '@/components/BackLink'
import { Posture, SkeletonPanel } from '@/components/primitives'
import DetailLayout from '@/components/DetailLayout'

function distinctText(value?: string | null, other?: string | null): string | null {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return null
  const otherText = typeof other === 'string' ? other.trim() : ''
  if (otherText && text.toLowerCase() === otherText.toLowerCase()) return null
  return text
}

export default function CompanyGroup() {
  const { groupId } = useParams()
  const [data, setData] = useState<any | null>(null)
  const [error, setError] = useState<any>(null)
  const [trackingAll, setTrackingAll] = useState(false)

  useEffect(() => {
    if (!groupId) return
    let cancelled = false
    clientApi.companyGroupDetail(groupId).then((x) => {
      if (cancelled) return
      setData(x)
    }).catch(setError)
    return () => { cancelled = true }
  }, [groupId])

  async function handleTrackAll() {
    if (!groupId) return
    setTrackingAll(true)
    try {
      await clientApi.selectGroups([groupId])
    } catch (ex) {
      setError(ex)
    } finally {
      setTrackingAll(false)
    }
  }

  if (!data && !error) return (
    <div className="container">
      <Topbar subtitle="Company Group" />
      <BackLink defaultTo="/universe/companies" defaultLabel="Companies" />
      <SkeletonPanel kind="report" count={4} />
    </div>
  )

  const group = data?.group || {}
  const members: any[] = data?.members || []

  return (
    <div className="container">
      <Topbar
        subtitle={group.name || 'Company Group'}
        right={
          <button className="btn primary" onClick={handleTrackAll} disabled={trackingAll}>
            {trackingAll ? 'Tracking...' : 'Track All'}
          </button>
        }
      />
      <BackLink defaultTo="/universe/companies" defaultLabel="Companies" />

      {error && <ErrorBox error={error} />}

      <DetailLayout.Hero
        kicker="Company group"
        title={group.name || 'Untitled group'}
        subtitle={group.description || undefined}
        pills={group.is_public ? <span className="pill ok">Public</span> : undefined}
      />

      <div className="grid" style={{ marginTop: 16 }}>
        {members.map((m: any) => {
          const c = m.company || {}
          const memoCall = m.memo_call || c.memo_call || null
          const forecastPosture = m.forecast_posture || c.forecast_posture || null
          const postureHeadline = forecastPosture?.headline || memoCall?.headline || memoCall?.summary
          const postureLabel = forecastPosture?.label || null
          const stanceDescription = distinctText(
            forecastPosture?.assessment || memoCall?.summary,
            forecastPosture?.headline || memoCall?.headline,
          )
          return (
            <Link key={c.id} to={`/universe/companies/${c.id}`} className="company-card">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{c.ticker}</div>
                  <div style={{ fontSize: 14 }}>{c.company_name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {c.sector && <span className="pill">{c.sector}</span>}
                </div>
              </div>
              {(postureHeadline || stanceDescription || postureLabel || forecastPosture?.most_likely_case || forecastPosture?.next_proof_point) && (
                <div className="company-card-posture">
                  <Posture
                    compact
                    headline={postureHeadline}
                    label={postureLabel}
                    summary={stanceDescription}
                    mostLikelyCase={forecastPosture?.most_likely_case || null}
                    nextProofPoint={forecastPosture?.next_proof_point || null}
                  />
                </div>
              )}
              {c.last_research_at && (
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Last research: {String(c.last_research_at).slice(0, 10)}
                </div>
              )}
            </Link>
          )
        })}

        {members.length === 0 && (
          <div className="card">
            <p className="muted">No companies in this group yet.</p>
          </div>
        )}
      </div>
    </div>
  )
}
